import { useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import Input from "../../components/form/input/InputField";
import { Search } from "lucide-react";
import AchievementTable from "@/components/tables/AchievementTable";
import AchievementService from "@/services/achievement.service";
import FlashcardService from "@/services/flashcard.service";
import { isValidURL } from "@/utils/validation";
import { useTranslation } from "react-i18next";

interface Achievement {
    _id: string;
    name: string;
    description: string;
    type: string;
    icon: string;
    rewardPoints: number;
}


export default function ListAchievements() {
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const { t } = useTranslation();

    const fetchAchievements = async (page: number, search?: string) => {
        try {
            setLoading(true);
            setError("");
            const response = await AchievementService.getAchievement({
                page: page,
                limit: 10,
                searchQuery: search,
            });
            if (response.data.achievements) {
                for (let it of response.data.achievements) {
                    if (!isValidURL(it.icon)) {
                        it.icon = it.icon ? await fetchSignedUrl(it.icon) : null;
                    }
                }
            }
            setAchievements(response.data.achievements || []);
            setTotalPages(Math.ceil((response.data.count || 0) / 10));
            setCurrentPage(page);
        } catch (err) {
            setError("Failed to fetch flashcards. Please try again later.");
            console.error("Error fetching flashcards:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    useEffect(() => {
        fetchAchievements(1, searchQuery);
    }, []);

    const handlePageChange = (page: number) => {
        fetchAchievements(page, searchQuery);
    };

    return (
        <>
            <PageMeta
                title={t("VIYUME - VIYUME Dashboard")}
                description={t("USER_MANAGEMENT")}
            />
            <div className="space-y-6">
                <ComponentCard title="">
                    <div className="mb-4 relative">
                        <Input
                            type="text"
                            placeholder={t("Search achievements")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onEnter={(value) => fetchAchievements(1, value)}
                            className="pl-10"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                    {loading ? (
                        <div className="animate-pulse">
                            <div className="space-y-4">
                                {[...Array(6)].map((_, index) => (
                                    <div key={index} className="flex items-center space-x-4 py-3">
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center p-4">{error}</div>
                    ) : (
                        <AchievementTable
                            achievements={achievements}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isLoading={loading}
                        />
                    )}
                </ComponentCard>
            </div>
        </>
    );
}
